import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSession, PullIndicator, usePullToRefresh } from 'gunsi-auth'
import { useEven } from '../hooks/useEven.jsx'
import { useEvenI18n } from '../i18n/even.js'
import { computePeopleBalances, initialsOf } from '../util/people.js'
import { itemCurrency } from '../util/balances.js'
import { getRatesForDates } from '../util/fx.js'
import { fmtMoney } from '../format.js'
import { Select } from '../components/Select.jsx'
import { BottomSheet } from '../components/BottomSheet.jsx'

// Preferenza della vista valute per questa pagina (null = separate)
const VIEW_LS = 'gunsi_even_peopleview_v1'
function loadView() {
  try { return localStorage.getItem(VIEW_LS) || null } catch { return null }
}
function saveView(cur) {
  try {
    if (cur) localStorage.setItem(VIEW_LS, cur)
    else localStorage.removeItem(VIEW_LS)
  } catch { /* private mode: la preferenza è un extra */ }
}

export function People() {
  const t = useEvenI18n()
  const navigate = useNavigate()
  const { session } = useSession()
  const { groups, expenses, settlements, loading, currency: userCurrency, refresh } = useEven()
  const { listRef, pull, refreshing, ready } = usePullToRefresh(refresh)
  const [viewCur, setViewCur] = useState(loadView)
  const [ratesByDate, setRatesByDate] = useState(null)
  const [ratesLoading, setRatesLoading] = useState(false)
  const [detail, setDetail] = useState(null)

  // Valute usate ovunque: decide se ha senso offrire la vista unificata
  const allCurs = useMemo(() => {
    const byGroup = Object.fromEntries(groups.map(g => [g.id, g]))
    const set = new Set()
    for (const it of [...expenses, ...settlements]) {
      set.add(itemCurrency(it, byGroup[it.groupId]))
    }
    return [...set]
  }, [groups, expenses, settlements])

  const viewCurs = useMemo(
    () => [...new Set([userCurrency, ...allCurs])].filter(Boolean),
    [userCurrency, allCurs],
  )
  const canConvert = allCurs.length > 1 ||
    (allCurs.length === 1 && Boolean(userCurrency) && allCurs[0] !== userCurrency)

  function changeViewCur(cur) {
    setViewCur(cur)
    saveView(cur)
  }

  useEffect(() => {
    if (!viewCur || !canConvert) return
    let cancelled = false
    setRatesLoading(true)
    const dates = [...expenses, ...settlements]
      .map(i => (i.date || '').slice(0, 10))
      .filter(Boolean)
    getRatesForDates(dates).then(map => {
      if (cancelled) return
      setRatesByDate(map)
      setRatesLoading(false)
    })
    return () => { cancelled = true }
  }, [viewCur, canConvert, expenses, settlements])

  const unified = Boolean(viewCur) && canConvert
  const waitingRates = unified && (ratesLoading || !ratesByDate)

  // I gruppi archiviati sono capitoli chiusi: le loro spese restano
  // consultabili, ma non pesano più sui saldi con le persone
  const liveGroups = useMemo(() => groups.filter(g => !g.archivedAt), [groups])

  const { people, totals } = useMemo(() => {
    if (waitingRates) return { people: [], totals: [] }
    return computePeopleBalances({
      groups: liveGroups, expenses, settlements,
      youUserId: session?.user?.id,
      target: unified ? viewCur : null,
      ratesByDate,
    })
  }, [liveGroups, expenses, settlements, session?.user?.id, unified, viewCur, ratesByDate, waitingRates])

  const open = people.filter(p => p.amounts.length > 0)
  const settled = people.filter(p => p.amounts.length === 0)
  const pfx = unified ? '≈ ' : ''

  if (loading) {
    return <div className="loading-screen"><div className="loading-spinner" /></div>
  }

  return (
    <main className="page page-people">
      <header className="page-head">
        <h1>{t.people.title}</h1>
      </header>

      <section className="people-totals">
        {waitingRates ? (
          <div className="loading-spinner small" />
        ) : totals.length === 0 ? (
          <div className="people-total-line status-even">{t.people.allSettled}</div>
        ) : (
          totals.map(x => (
            <div key={x.currency} className="people-total-line">
              {x.owed >= 0.005 && (
                <span className="status-owed">
                  {t.people.totalOwed} <strong>{pfx}{fmtMoney(x.owed, x.currency)}</strong>
                </span>
              )}
              {x.owed >= 0.005 && x.owe >= 0.005 && <span className="sep"> · </span>}
              {x.owe >= 0.005 && (
                <span className="status-owe">
                  {t.people.totalOwe} <strong>{pfx}{fmtMoney(x.owe, x.currency)}</strong>
                </span>
              )}
            </div>
          ))
        )}
      </section>

      {canConvert && (
        <div className="cur-view-row">
          <Select
            value={viewCur ?? ''}
            onChange={v => changeViewCur(v === '' ? null : v)}
            options={[
              { value: '', label: t.group.viewSeparate },
              ...viewCurs.map(c => ({ value: c, label: t.group.viewAllIn.replace('{cur}', c) })),
            ]}
            label={t.group.currencyView}
          />
        </div>
      )}

      <div className="ptr-wrap">
        <PullIndicator pull={pull} refreshing={refreshing} ready={ready} />
        <ul
          className="people-list"
          ref={listRef}
          style={{ transform: pull ? `translateY(${pull}px)` : undefined, transition: pull ? undefined : 'transform 180ms ease-out' }}
        >
          {people.length === 0 && !waitingRates && (
            <li className="empty-state"><p>{t.people.empty}</p></li>
          )}

          {open.map(p => (
            <PersonRow key={p.key} person={p} pfx={pfx} t={t} onOpen={() => setDetail(p)} />
          ))}

          {settled.length > 0 && open.length > 0 && (
            <li className="people-sep">{t.people.settledSection}</li>
          )}
          {settled.map(p => (
            <PersonRow key={p.key} person={p} pfx={pfx} t={t} onOpen={() => setDetail(p)} />
          ))}
        </ul>
      </div>

      <PersonSheet
        person={detail}
        pfx={pfx}
        t={t}
        onClose={() => setDetail(null)}
        // Stessa precauzione del passaggio dettaglio → modifica in GroupDetail:
        // lo sheet che si chiude fa history.back(), che su iOS può arrivare
        // dopo la navigazione e annullarla. Prima chiudi, poi naviga.
        onOpenGroup={(gid) => { setDetail(null); setTimeout(() => navigate(`/groups/${gid}`), 240) }}
      />
    </main>
  )
}

function PersonRow({ person, pfx, t, onOpen }) {
  // Credito in un gruppo e debito in un altro: il totale è un netto, e da
  // solo non dice quanto ci si deve davvero. Lo segnaliamo, il dettaglio è
  // a un tap di distanza.
  const withBalance = person.groups.filter(g => g.amounts.length > 0)
  const mixed = withBalance.length > 1 &&
    withBalance.some(g => g.amounts.some(a => a.net > 0)) &&
    withBalance.some(g => g.amounts.some(a => a.net < 0))
  return (
    <li className="person-card" onClick={onOpen}>
      <div className="person-avatar">{initialsOf(person.name)}</div>
      <div className="person-meta">
        <div className="person-name">
          {person.name}
          {!person.linked && <span className="person-ghost" title={t.people.ghostHint}>·</span>}
        </div>
        <div className="person-groups">
          {person.groups.map(g => `${g.emoji} ${g.name}`).join(' · ')}
        </div>
      </div>
      <div className="person-status">
        {person.amounts.length === 0 ? (
          <div className="status-line status-even">{t.groups.settled}</div>
        ) : (
          person.amounts.map(a => (
            <div key={a.currency} className={`status-line status-${a.net > 0 ? 'owed' : 'owe'}`}>
              {a.net > 0 ? t.people.owesYou : t.people.youOwe}{' '}
              {pfx}{fmtMoney(Math.abs(a.net), a.currency)}
            </div>
          ))
        )}
        {mixed && (
          <div className="person-mixed">
            {t.people.netOf.replace('{n}', String(withBalance.length))}
          </div>
        )}
      </div>
    </li>
  )
}

function PersonSheet({ person, pfx, t, onClose, onOpenGroup }) {
  return (
    <BottomSheet open={Boolean(person)} onClose={onClose} className="bsheet-select">
      {person && (
        <div className="select-sheet person-sheet">
          <header className="person-sheet-head">
            <div className="person-avatar lg">{initialsOf(person.name)}</div>
            <div>
              <h2>{person.name}</h2>
              <div className="person-sheet-sub">
                {person.amounts.length === 0
                  ? t.groups.settled
                  : person.amounts.map(a => (
                      <span key={a.currency} className={a.net > 0 ? 'status-owed' : 'status-owe'}>
                        {a.net > 0 ? t.people.owesYou : t.people.youOwe}{' '}
                        {pfx}{fmtMoney(Math.abs(a.net), a.currency)}
                      </span>
                    ))}
              </div>
            </div>
          </header>

          <div className="person-sheet-label">{t.people.byGroup}</div>
          <ul className="person-group-list">
            {person.groups.map(g => (
              <li key={g.id} className="person-group-row" onClick={() => onOpenGroup(g.id)}>
                <span className="pg-emoji">{g.emoji}</span>
                <span className="pg-name">{g.name}</span>
                <span className="pg-amounts">
                  {g.amounts.length === 0 ? (
                    <span className="status-even">{t.groups.settled}</span>
                  ) : (
                    // A parole e non col segno: con la stessa persona si può
                    // essere in credito in un gruppo e in debito in un altro,
                    // ed è lì che un "+" o "−" non basta a capire
                    g.amounts.map(a => (
                      <span key={a.currency} className={a.net > 0 ? 'status-owed' : 'status-owe'}>
                        {a.net > 0 ? t.people.owesYou : t.people.youOwe}{' '}
                        {pfx}{fmtMoney(Math.abs(a.net), a.currency)}
                      </span>
                    ))
                  )}
                </span>
              </li>
            ))}
          </ul>

          {!person.linked && (
            <p className="person-sheet-hint">{t.people.ghostHint}</p>
          )}
        </div>
      )}
    </BottomSheet>
  )
}
